import {
  CalendarOutlined,
  CommentOutlined,
  DashboardOutlined,
  DownOutlined,
  EditOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  HomeOutlined,
  KeyOutlined,
  MailOutlined,
  MobileOutlined,
  NumberOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { CustomerEditDrawer, RegPlate, SuccessButton } from '@components';
import { useCustomerSmsInvite, useView } from '@hooks';
import { Customer, Invoice, NotificationService, Vehicle } from '@services';
import { useStore } from '@stores';
import { Button, Descriptions, Divider, Modal, Popconfirm, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { SharedCard } from './SharedCard';

const iconStyle = (color: string): React.CSSProperties => ({
  fontSize: 14,
  color,
  marginTop: 2,
  flexShrink: 0,
});

const textStyle: React.CSSProperties = {
  wordBreak: 'break-word',
  fontSize: 14,
  lineHeight: '1.4',
  flex: 1,
};

const emptyStyle: React.CSSProperties = { ...textStyle, color: 'rgba(0, 0, 0, 0.45)' };

const labelStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6 };

// --- Shared sub-components ---

const Label = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <span style={labelStyle}>
    {icon} {text}
  </span>
);

const tagStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.5,
  lineHeight: 1,
  color: 'rgba(0, 0, 0, 0.45)',
  textAlign: 'center',
  minWidth: 14,
};

const CompactRow = ({
  icon,
  tooltip,
  value,
  fallback,
  tag,
}: {
  icon: React.ReactNode;
  tooltip: string;
  value?: string | null;
  fallback: string;
  tag?: string;
}) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
    <Tooltip title={tooltip}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: 20,
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        {icon}
        {tag && <span style={tagStyle}>{tag}</span>}
      </div>
    </Tooltip>
    <span style={value ? textStyle : emptyStyle}>{value || fallback}</span>
  </div>
);

// --- Field definitions ---

interface FieldDef {
  icon: React.ReactNode;
  label: string;
  color: string;
  value?: string | null;
  fallback: string;
  bold?: boolean;
  tag?: string;
}

const getCustomerFields = (customer: Customer): FieldDef[] => [
  {
    icon: <ShopOutlined />,
    label: 'Business name',
    color: '#722ed1',
    value: customer.businessName,
    fallback: 'No company provided',
  },
  {
    icon: <NumberOutlined />,
    label: 'Postcode',
    color: '#eb2f96',
    value: customer.postcode,
    fallback: 'No postcode provided',
  },
  {
    icon: <MailOutlined />,
    label: 'Email',
    color: '#1677ff',
    value: customer.email,
    fallback: 'No email provided',
  },
  {
    icon: <EnvironmentOutlined />,
    label: 'City',
    color: '#f5222d',
    value: customer.city,
    fallback: 'No city provided',
  },
  {
    icon: <MobileOutlined />,
    label: 'Phone',
    color: '#52c41a',
    value: customer.phone,
    fallback: 'No phone provided',
  },
  {
    icon: <HomeOutlined />,
    label: 'Address',
    color: '#fa541c',
    value: customer.address,
    fallback: 'No address provided',
  },
  {
    icon: <PhoneOutlined />,
    label: 'Landline',
    color: '#fa8c16',
    value: customer.landline,
    fallback: 'No landline provided',
  },
  {
    icon: <GlobalOutlined />,
    label: 'Country',
    color: '#13c2c2',
    value: customer.country,
    fallback: 'No country provided',
  },
];

const getDefaultExtraFields = (customer: Customer): FieldDef[] => [
  {
    icon: <HomeOutlined />,
    label: 'Address 2',
    color: '#fa541c',
    value: customer.secondAddress,
    fallback: 'No address provided',
  },
  {
    icon: <HomeOutlined />,
    label: 'Address 3',
    color: '#fa541c',
    value: customer.thirdAddress,
    fallback: 'No address provided',
  },
];

const getVehicleFields = (vehicle: Vehicle): FieldDef[] => [
  {
    icon: <DashboardOutlined />,
    label: 'Engine size',
    color: '#eb2f96',
    value: vehicle.combinedEngineCapacity,
    fallback: '—',
    tag: 'CC',
  },
  {
    icon: <KeyOutlined />,
    label: 'VIN No.',
    color: '#13c2c2',
    value: vehicle.combinedVin,
    fallback: '—',
    tag: 'VIN',
  },
  {
    icon: <CalendarOutlined />,
    label: 'First reg date',
    color: '#fa541c',
    value:
      vehicle.dateFirstRegistered && dayjs(vehicle.dateFirstRegistered).isValid()
        ? dayjs(vehicle.dateFirstRegistered).format('DD/MM/YYYY')
        : undefined,
    fallback: '—',
    tag: 'REG',
  },
  {
    icon: <SafetyCertificateOutlined />,
    label: 'MOT date',
    color: '#f5222d',
    value:
      vehicle.motExpiryDate &&
      dayjs(vehicle.motExpiryDate).isValid() &&
      !dayjs(vehicle.motExpiryDate).isSame(dayjs(0))
        ? dayjs(vehicle.motExpiryDate).format('DD/MM/YYYY')
        : undefined,
    fallback: 'Exempt from MOT',
    tag: 'MOT',
  },
];

// --- Main component ---

interface IProps {
  customer: Customer;
  vehicle?: Vehicle;
  compact?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  bordered?: boolean;
  hideHeader?: boolean;
  fixedWidth?: boolean;
  onOpenChat?: () => void;
  invoice?: Invoice;
}

export const CustomerDetailsCard: React.FC<IProps> = observer(
  ({ customer, vehicle, compact, collapsible = false, defaultExpanded = true, bordered = true, hideHeader = false, fixedWidth = false, onOpenChat, invoice }) => {
    const { isMobile } = useView();
    const { customerStore } = useStore();
    const { customerInvite, loading: customerInviteLoading } = useCustomerSmsInvite(customer?.id);
    const [readyForPickupLoading, setReadyForPickupLoading] = useState(false);
    const [smsInviteModalOpen, setSmsInviteModalOpen] = useState(false);
    const [smsSending, setSmsSending] = useState(false);
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [expanded, setExpanded] = useState(defaultExpanded);

    const handleSendSms = useCallback(async () => {
      if (!customer) return;
      setSmsSending(true);
      await NotificationService.sendCustomerSmsInvitation(customer.id).then(() => {
        setSmsInviteModalOpen(false);
        customerStore.setCustomerSmsInvite({
          id: customer.id,
          type: 'SmsInvite',
          customerId: customer.id,
          creationDate: dayjs().format(),
        });
      });
      setSmsSending(false);
    }, [customer, customerStore]);

    const onReadyForPickupConfirm = useCallback(async () => {
      if (!invoice?.registrationNumber || !invoice?.id) return;
      setReadyForPickupLoading(true);
      try {
        await NotificationService.readyForPickup({
          registrationNumber: invoice.registrationNumber,
          sendPush: true,
          invoiceId: invoice.id,
        });
      } finally {
        setReadyForPickupLoading(false);
      }
    }, [invoice]);

    const nameTag = (
      <Tag color={customer.userId ? 'green' : 'red'} style={{ margin: 0 }}>
        {customer.userId ? 'App user' : 'Not on app'}
      </Tag>
    );

    const editButton = (
      <Button
        shape="circle"
        aria-label="Edit"
        style={{ fontSize: 16, border: 'none' }}
        onClick={() => setEditDrawerOpen(true)}
        icon={<EditOutlined />}
      />
    );

    const chatButton = customer.userId !== null && onOpenChat && (
      <Button
        shape="circle"
        aria-label="Chat"
        style={{ fontSize: 16, border: 'none' }}
        onClick={onOpenChat}
        icon={<CommentOutlined />}
      />
    );

    const customerFields = getCustomerFields(customer);
    const vehicleFields = vehicle ? getVehicleFields(vehicle) : [];

    const smsInviteButton = customer && customer.userId === null && customer.phone && (
      <SuccessButton
        size="small"
        onClick={() => setSmsInviteModalOpen(true)}
        disabled={!!customerInvite}
        loading={customerInviteLoading}
      >
        {!!customerInvite ? 'Invited' : 'SMS Invite'}
      </SuccessButton>
    );

    const readyForPickupButton = invoice && (
      <Popconfirm
        title="Inform the customer that their vehicle is ready for pick up?"
        okText="Yes, inform"
        cancelText="Don't inform"
        onConfirm={onReadyForPickupConfirm}
        okButtonProps={{ loading: readyForPickupLoading }}
      >
        <Button type="primary" size="small" loading={readyForPickupLoading}>
          Ready for pickup
        </Button>
      </Popconfirm>
    );

    const modals = (
      <>
        <Modal
          title={`Send an SMS invite to download the Umoto app to ${customer.fullname} on ${customer.phone}`}
          open={smsInviteModalOpen}
          onCancel={() => setSmsInviteModalOpen(false)}
          onOk={handleSendSms}
          okText="Yes, send"
          cancelText="Cancel"
          confirmLoading={smsSending}
        />
        <CustomerEditDrawer
          open={editDrawerOpen}
          customer={customer}
          onClose={() => setEditDrawerOpen(false)}
        />
      </>
    );

    if (compact) {
      return (
        <>
          <SharedCard style={{ height: '100%' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'space-between',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <strong style={{ fontSize: 14 }}>{customer.fullname}</strong>
                {nameTag}
                {isMobile && <span style={{ marginLeft: 'auto' }}>{editButton}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {readyForPickupButton}
                {smsInviteButton}
                {!isMobile && editButton}
                {chatButton}
              </div>
            </div>
            <Divider style={{ margin: '13px 0' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 8px' }}>
              {customerFields.map((f, i) => (
                <CompactRow
                  key={i}
                  icon={React.cloneElement(f.icon as React.ReactElement<{ style?: React.CSSProperties }>, {
                    style: iconStyle(f.color),
                  })}
                  tooltip={f.label}
                  value={f.value}
                  fallback={f.fallback}
                  tag={f.tag}
                />
              ))}
            </div>
            {vehicle?.registrationNumber && (
              <>
                <Divider style={{ margin: '13px 0' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <RegPlate>{vehicle.registrationNumber}</RegPlate>
                  {(vehicle.combinedMake || vehicle.combinedModel) && (
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(0, 0, 0, 0.65)' }}>
                      {`${vehicle.combinedMake || ''} ${vehicle.combinedModel || ''}`.trim()}
                    </span>
                  )}
                </div>
              </>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 8px' }}>
              {vehicleFields.map((f, i) => (
                <CompactRow
                  key={i}
                  icon={React.cloneElement(f.icon as React.ReactElement<{ style?: React.CSSProperties }>, {
                    style: iconStyle(f.color),
                  })}
                  tooltip={f.label}
                  value={f.value}
                  fallback={f.fallback}
                  tag={f.tag}
                />
              ))}
            </div>
          </SharedCard>
          {modals}
        </>
      );
    }

    const customerFieldsFull = [
      ...customerFields.slice(0, 6),
      ...getDefaultExtraFields(customer),
      ...customerFields.slice(6),
    ];

    const descProps = {
      size: 'small' as const,
      column: { xs: 1, sm: 1, md: 3 } as const,
      bordered: true,
      styles: {
        label: { fontWeight: 'bold' as const, ...(fixedWidth && { width: 140 }) },
        content: fixedWidth ? { width: 200 } : undefined,
      },
    };

    const renderDescItems = (fields: FieldDef[]) => (
      <>
        {fields.map((f, i) => (
          <Descriptions.Item
            key={i}
            label={
              <Label
                icon={React.cloneElement(f.icon as React.ReactElement<{ style?: React.CSSProperties }>, {
                  style: { color: f.color },
                })}
                text={f.label}
              />
            }
          >
            {f.value ? (
              f.bold ? (
                <strong>{f.value}</strong>
              ) : (
                f.value
              )
            ) : (
              <span style={emptyStyle}>{f.fallback}</span>
            )}
          </Descriptions.Item>
        ))}
        {fields.length % 3 !== 0 &&
          Array.from({ length: 3 - (fields.length % 3) }).map((_, i) => (
            <Descriptions.Item key={`spacer-${i}`} label={<span />}>
              {' '}
            </Descriptions.Item>
          ))}
      </>
    );

    const cardContent = (
      <>
        {!hideHeader && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 8,
              marginBottom: collapsible && !expanded ? 8 : 12,
              cursor: collapsible ? 'pointer' : undefined,
            }}
            onClick={collapsible ? () => setExpanded((prev) => !prev) : undefined}
          >
            <strong style={{ fontSize: 16 }}>{customer.fullname}</strong>
            {nameTag}
            {smsInviteButton}
            {readyForPickupButton}
            <span onClick={(e) => e.stopPropagation()}>{editButton}</span>
            {chatButton && <span onClick={(e) => e.stopPropagation()}>{chatButton}</span>}
            {collapsible && (
              <span style={{ marginLeft: 'auto' }}>
                {expanded ? <UpOutlined /> : <DownOutlined />}
              </span>
            )}
          </div>
        )}
        {(!collapsible || expanded) && (
          <>
            <Descriptions {...descProps} style={{ marginTop: 8 }}>
              {renderDescItems(customerFieldsFull)}
            </Descriptions>
            {vehicle?.registrationNumber && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <RegPlate>{vehicle.registrationNumber}</RegPlate>
                  {(vehicle.combinedMake || vehicle.combinedModel) && (
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0, 0, 0, 0.65)' }}>
                      {`${vehicle.combinedMake || ''} ${vehicle.combinedModel || ''}`.trim()}
                    </span>
                  )}
                </div>
              </>
            )}
            <Descriptions {...descProps}>{renderDescItems(vehicleFields)}</Descriptions>
          </>
        )}
      </>
    );

    return (
      <>
        {bordered ? <SharedCard size="small">{cardContent}</SharedCard> : cardContent}
        {modals}
      </>
    );
  }
);
